import React, { useState, useEffect, Suspense } from "react";
import {
  IconButton,
  Tooltip,
  Divider,
  Box,
  CssBaseline,
  useMediaQuery,
  List,
  Typography,
  Button,
} from "@mui/material";
import PageLoader from "../theme/GenericLoader/PageLoader";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Apps as AppsIcon,
} from "@mui/icons-material";
import { getMenuConfig } from "./MenuConfig";
import {
  MOBILE_BREAKPOINT,
  LayoutContainer,
  SidebarContainer,
  DesktopDrawer,
  MobileDrawer,
  DrawerHeader,
  LogoSection,
  LogoText,
  ToggleButton,
  UserSection,
  UserAvatar,
  UserInfo,
  SectionTitle,
  DrawerFooter,
  MenuContent,
  StyledListItem,
  StyledListItemIcon,
  StyledListItemText,
  StyledBadge,
  MobileMenuButton,
  MobileCloseButton,
  MainContent,
  CustomBackdrop
} from "./DrawerStyles";
import { useTheme } from "@mui/material/styles";
import { Link, useLocation, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import Store from "../index";
import helpers from "../UtilitiesFunctions";
import ThemeToggle from "../theme/ThemeToggle";

function ResponsiveDrawer(props) {
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));

  const {
    userInfo,
    userRole,
    authenticated,
    cartlength,
    appType,
  } = props;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [isMobile]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleMobileDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMobileDrawerClose = () => setMobileOpen(false);
  const handleDesktopDrawerToggle = () => setDesktopOpen(!desktopOpen);

  const logout = async () => {
    helpers.clearData();
    history.push("/");
  };

  const isAdmin = userRole === "Admin" || userRole === "ADMIN";
  const isManager = userRole === "Manager" || userRole === "MANAGER";
  const isAgent = userRole === "Agent" || userRole === "AGENT";

  const getCurrentMenuItems = () => {
    const bussinessType = Store.getState()?.globalState?.bussinessType;
    return getMenuConfig(userRole, appType, bussinessType, cartlength);
  };

  const renderMenuItem = (item, index, drawerOpen) => {
    const isItemActive = isActive(item.path);

    if (item.action) {
      const actionItem = (
        <StyledListItem
          onClick={() => {
            if (item.action === "logout") logout();
            if (isMobile) handleMobileDrawerClose();
          }}
          open={drawerOpen}
          key={index}
        >
          <StyledListItemIcon open={drawerOpen}>
            {item.badge ? <StyledBadge badgeContent={item.badge}>{item.icon}</StyledBadge> : item.icon}
          </StyledListItemIcon>
          <StyledListItemText primary={item.name} open={drawerOpen} />
        </StyledListItem>
      );

      if (!drawerOpen) {
        return (
          <Tooltip key={index} title={item.name} placement="right" arrow>
            {actionItem}
          </Tooltip>
        );
      }
      return actionItem;
    }

    const listItem = (
      <StyledListItem
        component={Link}
        to={item.path}
        active={isItemActive}
        open={drawerOpen}
        key={index}
        onClick={() => isMobile && handleMobileDrawerClose()}
      >
        <StyledListItemIcon open={drawerOpen}>
          {item.badge ? <StyledBadge badgeContent={item.badge}>{item.icon}</StyledBadge> : item.icon}
        </StyledListItemIcon>
        <StyledListItemText primary={item.name} open={drawerOpen} />
      </StyledListItem>
    );

    if (!drawerOpen) {
      return (
        <Tooltip key={index} title={item.name} placement="right" arrow>
          {listItem}
        </Tooltip>
      );
    }
    return listItem;
  };

  const drawer = (drawerOpen, isMobileDrawer = false) => (
    <>
      <DrawerHeader open={drawerOpen}>
        <LogoSection open={drawerOpen}>
          <img
            src={`${process.env.PUBLIC_URL}/Website.png`}
            alt="WEBSITE"
            style={{
              height: drawerOpen ? '35px' : '35px',
              width: 'auto',
              transition: 'height 0.2s ease'
            }}
          />
          <LogoText variant="h6" open={drawerOpen}>
            WEBSITE
          </LogoText>
        </LogoSection>
        {isMobileDrawer && (
          <MobileCloseButton onClick={handleMobileDrawerClose}>
            <CloseIcon />
          </MobileCloseButton>
        )}
      </DrawerHeader>

      <MenuContent>
        <Box sx={{ py: 4 }}>
          {getCurrentMenuItems()
            .filter(section => section.section !== "ACCOUNT")
            .map((section, sectionIndex) => (
              <Box key={sectionIndex}>
                <SectionTitle open={drawerOpen}>{section.section}</SectionTitle>
                <List disablePadding sx={{ mb: 1 }}>
                  {section.items.map((item, itemIndex) =>
                    renderMenuItem(item, `${sectionIndex}-${itemIndex}`, drawerOpen)
                  )}
                </List>
                {sectionIndex < getCurrentMenuItems().filter(s => s.section !== "ACCOUNT").length - 1 && drawerOpen && (
                  <Box sx={{ mx: 2, my: 0.5 }}>
                    <Divider sx={{ backgroundColor: theme.palette.divider }} />
                  </Box>
                )}
              </Box>
            ))}
        </Box>
      </MenuContent>

      <DrawerFooter>
        <Box sx={{ p: drawerOpen ? 2 : 1, pb: 0, display: 'flex', justifyContent: 'center' }}>
          {drawerOpen ? (
            <Button
              fullWidth
              variant="outlined"
              href="https://app.hulmsolutions.com/dashboard"
              target="_blank"
              startIcon={<AppsIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: theme.palette.primary.dark,
                borderColor: theme.palette.primary.dark,
              }}
            >
              Go to Apps
            </Button>
          ) : (
            <Tooltip title="Go to Apps" placement="right">
              <IconButton href="https://app.hulmsolutions.com/dashboard" target="_blank">
                <AppsIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {userInfo && (
          <UserSection open={drawerOpen}>
            <UserAvatar>
              {userInfo.name ? userInfo.name.charAt(0).toUpperCase() : <PersonIcon />}
            </UserAvatar>
            <UserInfo open={drawerOpen}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {userInfo.name || userInfo.given_name || "Super Admin"}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {userRole || "Admin"}
              </Typography>
            </UserInfo>
            <Box sx={{ ml: 'auto' }}>
              <ThemeToggle sx={{ width: 32, height: 32 }} />
            </Box>
          </UserSection>
        )}

        <List disablePadding sx={{ mb: 0 }}>
          {getCurrentMenuItems()
            .find(section => section.section === "ACCOUNT")
            ?.items.map((item, index) => renderMenuItem(item, `footer-${index}`, drawerOpen))}
        </List>
      </DrawerFooter>
    </>
  );

  useEffect(() => {
    if (mobileOpen && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [mobileOpen, isMobile]);

  return (
    <LayoutContainer>
      <CssBaseline />

      {isMobile && (
        <MobileMenuButton onClick={handleMobileDrawerToggle}>
          <MenuIcon />
        </MobileMenuButton>
      )}

      {authenticated && (isAdmin || isManager || isAgent) && (
        <>
          <SidebarContainer>
            <DesktopDrawer open={desktopOpen}>
              {drawer(desktopOpen, false)}
              <ToggleButton onClick={handleDesktopDrawerToggle} size="small" open={desktopOpen}>
                {desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </ToggleButton>
            </DesktopDrawer>
          </SidebarContainer>

          {isMobile && (
            <>
              <CustomBackdrop open={mobileOpen} onClick={handleMobileDrawerClose} />
              <MobileDrawer variant="temporary" open={mobileOpen} onClose={handleMobileDrawerClose}>
                {drawer(true, true)}
              </MobileDrawer>
            </>
          )}
        </>
      )}

      <MainContent isMobile={isMobile}>
        <Suspense fallback={<PageLoader />}>
          <Box sx={{ padding: 0, minHeight: "100vh" }}>
            <Box>{props.children}</Box>
          </Box>
        </Suspense>
      </MainContent>
    </LayoutContainer>
  );
}

ResponsiveDrawer.defaultProps = {
  authenticated: false,
  userInfo: { preferred_username: "guest" },
};

export default ResponsiveDrawer;